import { 
    fromUtf8,
} from "@aws-sdk/util-utf8-node"

import { 
    LambdaClient, 
    InvokeCommand, 
    LogType, 
} from "@aws-sdk/client-lambda"


import {
    getUrl,
    uploadData,    
    downloadData,
    remove,
} from "aws-amplify/storage"


import {
    Amplify,
} from "aws-amplify"

import {
    Buffer,
} from "buffer"

import Observable from "zen-observable"

import * as mutations from "../../graphql/mutations"
import * as queries from "../../graphql/queries"
import * as subscriptions from "../../graphql/subscriptions"

import {
    GraphQLResult,
    GraphQLOptionsV6,
} from "@aws-amplify/api-graphql"

import {
    fetchUserAttributes,
    FetchUserAttributesOutput,
    fetchAuthSession,
    updateUserAttributes,
    confirmUserAttribute,
    updatePassword,
} from "aws-amplify/auth"

import {
    generateClient,
} from "aws-amplify/api"

import DGElement from "../DGElement.m"

const REGION = "us-east-2"

import awsExports from "../../aws-exports"

Amplify.configure(awsExports)

export const amplify = generateClient()

/**
 * A class that contains methods for interacting with the Amplify API
 * 
 * @class
 * @extends DGElement
 */
export class DGAmplifyTools extends DGElement {
    /*** Members ***/ 

    #client: typeof amplify
    #IS_DEV: boolean
    /*** Protected Methods ***/

    /**
     * Executes a graphQL statement. Automatically determines whether the 
     * statement is a mutation, query, or subscription.
     * 
     * @param {string} stmt - The graphQL statement to execute
     * @param {object} args - The arguments to pass to the statement
     * @returns {Promise<GraphQLResult<any> | Error | Observable<object>} - The API response or error
     */
    public executeGQL = async (stmt: string, args: object = {}): Promise<object | Error | Observable<object>> => {
        let method : typeof mutations | typeof queries | typeof subscriptions
        if (stmt.substring(0, 4) === "list" || stmt.substring(0, 3) === "get") {
            method = queries
        }
        else if (stmt.substring(0, 6) ==="create" || stmt.substring(0, 6) === "update" || stmt.substring(0, 6) === "delete") {
            method = mutations
        }
        else if (stmt.substring(0, 8) === "onCreate" || stmt.substring(0, 8) === "onUpdate" || stmt.substring(0, 8) === "onDelete") {
            method = subscriptions
        }
        else {
            return new Error(`Illegal statement "${stmt}." Statements must begin with list, get, update, delete, onCreate, onUpdate, or onDelete.`)
        }

        if (!method[stmt]) {
            return new Error(`Illegal statement "${stmt}." Statement must be a valid mutation, subscription, or query.`)
        }
        let resp: GraphQLResult<any> | Error | Observable<object>
        try {
            resp = await this.client.graphql({
                query: method[stmt],
                variables: args,
            }) as GraphQLResult<any>
        } catch(e) {
            return e
        }
        return method === subscriptions ? resp : resp?.data[stmt]
    }

    /**
     * Executes a lambda function
     * 
     * @param {string} functionName - The name of the lambda function to execute
     * @param {object} functionArgs - The arguments to pass to the lambda function
     * @returns {Promise<string>} - The API response or error
     */
    public executeLambda = async (functionName: string, functionArgs: any): Promise<{result: string, logs: string}> => {
        const [name, subname] = functionName.split(".")
        if (!(name && functionArgs)) {
            throw new Error("Cannot call a lambda without a name and args")
        }
    
        const args = functionArgs
        if (subname) {
            args["desiredFunctionName"] = subname
        }
    
        const fullName = name + ((this.#IS_DEV) ? "-dev" : "-main")
    
        const sess = await fetchAuthSession()
        const credentials = sess.credentials
        
        const client = new LambdaClient({
            region: REGION,
            credentials,
        })

        const info = await fetchUserAttributes()
        args["cognitoIdentityId"] = sess.identityId
        args["userID"] = info.sub
        args["userName"] = info.sub
        if (this.#IS_DEV) {
            console.log(`Calling lambda ${fullName} with args: ${JSON.stringify(args)}`)
        }
        const invocation = new InvokeCommand({
            FunctionName: fullName,
            Payload: fromUtf8(JSON.stringify(args)),
            LogType: LogType.Tail
        })

        // async/await.
        try {
            const { Payload, LogResult } = await client.send(invocation)
            const result = Buffer.from(Payload).toString()
            const logs = Buffer.from(LogResult, "base64").toString()
            if (this.#IS_DEV) {
                console.log(logs)
            }
            return { logs, result }
        } catch (error) {
            // error handling.
            throw new Error(error)
        }
    }

    /*** Public Methods ***/

    public constructor(isDev: boolean) {
        super()

        this.#IS_DEV = isDev
        this.#client = amplify
    }

    /**
     * Get all the parameters for a lambda function
     * 
     * @param {string} functionName - The name of the lambda function to execute
     * @returns {Promise<object>} - @see this.executeLambda
     */
    public getLambdaParams = async (functionName: string): Promise<{result: string, logs: string}> => {
        return await this.executeLambda(
            functionName,
            { 
                listAllParams: true 
            }
        )
    }

    public get client() : typeof amplify {
        return this.#client
    }

    public getTaskPDF = async (task: string): Promise<string> => {
        const path = `public/${task}.pdf`
        const file = await getUrl({
            path,
        })

        if (file) 
            return file.url.toString()
        
        return null
    }
}

/**
 * The singleton instance of the DGAmplifyTools class
 */
const DGAmplify = new DGAmplifyTools(true)

export default DGAmplify